import React from 'react'

import {
    Drawer,
    Box,
    Typography,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    TextField,
    Button,
    Divider,
    Chip,
    InputAdornment,
    IconButton,
} from '@material-ui/core'
import { AttachMoney, Person, Close } from '@material-ui/icons'

const STATUS_OPTIONS = ['draft', 'sent', 'paid', 'overdue', 'voided', 'partial']

interface TenantBillFilters {
    status?: string
    search?: string
    dueDateStart?: string
    dueDateEnd?: string
    minAmount?: string
    maxAmount?: string
    paymentStatus?: string
    isOverdue?: string
    minAmountPaid?: string
    maxAmountPaid?: string
    minBalanceDue?: string
    maxBalanceDue?: string
    createdDateStart?: string
    createdDateEnd?: string
}

interface TenantBillFilterDrawerProps {
    open: boolean
    onClose: () => void
    filters: TenantBillFilters
    onFilterChange: (field: keyof TenantBillFilters, value: string) => void
    onApply: () => void
    onReset: () => void
    onQuickFilter: (preset: string) => void
}

const TenantBillFilterDrawer: React.FC<TenantBillFilterDrawerProps> = ({
    open,
    onClose,
    filters,
    onFilterChange,
    onApply,
    onReset,
    onQuickFilter,
}) => {
    return (
        <Drawer anchor="right" open={open} onClose={onClose}>
            <Box
                width={400}
                display="flex"
                flexDirection="column"
                height="100%"
            >
                {/* Header - Always visible */}
                <Box p={3} pb={2}>
                    <Box
                        display="flex"
                        alignItems="center"
                        justifyContent="space-between"
                        marginBottom={2}
                    >
                        <Typography variant="h6">Filters</Typography>
                        <IconButton onClick={onClose} size="small">
                            <Close />
                        </IconButton>
                    </Box>
                    <Divider />
                </Box>

                {/* Scrollable Filters Section */}
                <Box
                    flex={1}
                    overflow="auto"
                    px={3}
                    style={{
                        paddingTop: 0,
                        paddingBottom: 16,
                    }}
                >
                    {/* Quick Filter Presets */}
                    <Typography
                        variant="subtitle2"
                        style={{ marginBottom: 8, fontWeight: 600 }}
                    >
                        Quick Filters
                    </Typography>
                    <Box
                        style={{
                            display: 'flex',
                            flexWrap: 'wrap',
                            gap: 8,
                            marginBottom: 16,
                        }}
                    >
                        <Chip
                            label="Overdue"
                            size="small"
                            clickable
                            onClick={() => onQuickFilter('overdue')}
                            color="secondary"
                            variant="outlined"
                        />
                        <Chip
                            label="Unpaid"
                            size="small"
                            clickable
                            onClick={() => onQuickFilter('unpaid')}
                            color="default"
                            variant="outlined"
                        />
                        <Chip
                            label="Paid"
                            size="small"
                            clickable
                            onClick={() => onQuickFilter('paid')}
                            color="primary"
                            variant="outlined"
                        />
                        <Chip
                            label="This Month"
                            size="small"
                            clickable
                            onClick={() => onQuickFilter('thisMonth')}
                            variant="outlined"
                        />
                        <Chip
                            label="Last Month"
                            size="small"
                            clickable
                            onClick={() => onQuickFilter('lastMonth')}
                            variant="outlined"
                        />
                        <Chip
                            label="This Year"
                            size="small"
                            clickable
                            onClick={() => onQuickFilter('thisYear')}
                            variant="outlined"
                        />
                    </Box>

                    {/* Basic Filters */}
                    <Typography
                        variant="subtitle2"
                        style={{ marginBottom: 8, fontWeight: 600 }}
                    >
                        Basic Filters
                    </Typography>

                    <FormControl fullWidth margin="normal">
                        <InputLabel shrink>Status</InputLabel>
                        <Select
                            value={filters.status || ''}
                            onChange={(e) =>
                                onFilterChange(
                                    'status',
                                    e.target.value as string,
                                )
                            }
                            displayEmpty
                            renderValue={(value) =>
                                (value as string) || 'All Statuses'
                            }
                        >
                            <MenuItem value="">All Statuses</MenuItem>
                            {STATUS_OPTIONS.map((status) => (
                                <MenuItem key={status} value={status}>
                                    {status.charAt(0).toUpperCase() +
                                        status.slice(1)}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>

                    <TextField
                        fullWidth
                        label="Search"
                        value={filters.search || ''}
                        onChange={(e) =>
                            onFilterChange('search', e.target.value)
                        }
                        margin="normal"
                        placeholder="Search by tenant or unit name"
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start">
                                    <Person fontSize="small" />
                                </InputAdornment>
                            ),
                        }}
                    />

                    {/* Payment Status Filters */}
                    <Typography
                        variant="subtitle2"
                        style={{
                            marginTop: 16,
                            marginBottom: 8,
                            fontWeight: 600,
                        }}
                    >
                        Payment Status
                    </Typography>

                    <FormControl fullWidth margin="normal">
                        <InputLabel shrink>Payment Status</InputLabel>
                        <Select
                            value={filters.paymentStatus || ''}
                            onChange={(e) =>
                                onFilterChange(
                                    'paymentStatus',
                                    e.target.value as string,
                                )
                            }
                            displayEmpty
                        >
                            <MenuItem value="">All Payment Statuses</MenuItem>
                            <MenuItem value="paid">Fully Paid</MenuItem>
                            <MenuItem value="partial">Partially Paid</MenuItem>
                            <MenuItem value="unpaid">Unpaid</MenuItem>
                        </Select>
                    </FormControl>

                    <FormControl fullWidth margin="normal">
                        <InputLabel shrink>Overdue Status</InputLabel>
                        <Select
                            value={filters.isOverdue || ''}
                            onChange={(e) =>
                                onFilterChange(
                                    'isOverdue',
                                    e.target.value as string,
                                )
                            }
                            displayEmpty
                        >
                            <MenuItem value="">All Bills</MenuItem>
                            <MenuItem value="true">Overdue Only</MenuItem>
                            <MenuItem value="false">Not Overdue</MenuItem>
                        </Select>
                    </FormControl>

                    {/* Date Filters */}
                    <Typography
                        variant="subtitle2"
                        style={{
                            marginTop: 16,
                            marginBottom: 8,
                            fontWeight: 600,
                        }}
                    >
                        Date Filters
                    </Typography>

                    <TextField
                        fullWidth
                        label="Due Date From"
                        type="date"
                        value={filters.dueDateStart || ''}
                        onChange={(e) =>
                            onFilterChange('dueDateStart', e.target.value)
                        }
                        margin="normal"
                        InputLabelProps={{
                            shrink: true,
                        }}
                    />

                    <TextField
                        fullWidth
                        label="Due Date To"
                        type="date"
                        value={filters.dueDateEnd || ''}
                        onChange={(e) =>
                            onFilterChange('dueDateEnd', e.target.value)
                        }
                        margin="normal"
                        InputLabelProps={{
                            shrink: true,
                        }}
                    />

                    <TextField
                        fullWidth
                        label="Created Date From"
                        type="date"
                        value={filters.createdDateStart || ''}
                        onChange={(e) =>
                            onFilterChange('createdDateStart', e.target.value)
                        }
                        margin="normal"
                        InputLabelProps={{
                            shrink: true,
                        }}
                    />

                    <TextField
                        fullWidth
                        label="Created Date To"
                        type="date"
                        value={filters.createdDateEnd || ''}
                        onChange={(e) =>
                            onFilterChange('createdDateEnd', e.target.value)
                        }
                        margin="normal"
                        InputLabelProps={{
                            shrink: true,
                        }}
                    />

                    {/* Amount Filters */}
                    <Typography
                        variant="subtitle2"
                        style={{
                            marginTop: 16,
                            marginBottom: 8,
                            fontWeight: 600,
                        }}
                    >
                        Amount Filters
                    </Typography>

                    <TextField
                        fullWidth
                        label="Total Amount Min"
                        type="number"
                        value={filters.minAmount || ''}
                        onChange={(e) =>
                            onFilterChange('minAmount', e.target.value)
                        }
                        margin="normal"
                        placeholder="0.00"
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start">
                                    <AttachMoney fontSize="small" />
                                </InputAdornment>
                            ),
                        }}
                    />

                    <TextField
                        fullWidth
                        label="Total Amount Max"
                        type="number"
                        value={filters.maxAmount || ''}
                        onChange={(e) =>
                            onFilterChange('maxAmount', e.target.value)
                        }
                        margin="normal"
                        placeholder="0.00"
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start">
                                    <AttachMoney fontSize="small" />
                                </InputAdornment>
                            ),
                        }}
                    />

                    <TextField
                        fullWidth
                        label="Amount Paid Min"
                        type="number"
                        value={filters.minAmountPaid || ''}
                        onChange={(e) =>
                            onFilterChange('minAmountPaid', e.target.value)
                        }
                        margin="normal"
                        placeholder="0.00"
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start">
                                    <AttachMoney fontSize="small" />
                                </InputAdornment>
                            ),
                        }}
                    />

                    <TextField
                        fullWidth
                        label="Amount Paid Max"
                        type="number"
                        value={filters.maxAmountPaid || ''}
                        onChange={(e) =>
                            onFilterChange('maxAmountPaid', e.target.value)
                        }
                        margin="normal"
                        placeholder="0.00"
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start">
                                    <AttachMoney fontSize="small" />
                                </InputAdornment>
                            ),
                        }}
                    />

                    <TextField
                        fullWidth
                        label="Balance Due Min"
                        type="number"
                        value={filters.minBalanceDue || ''}
                        onChange={(e) =>
                            onFilterChange('minBalanceDue', e.target.value)
                        }
                        margin="normal"
                        placeholder="0.00"
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start">
                                    <AttachMoney fontSize="small" />
                                </InputAdornment>
                            ),
                        }}
                    />

                    <TextField
                        fullWidth
                        label="Balance Due Max"
                        type="number"
                        value={filters.maxBalanceDue || ''}
                        onChange={(e) =>
                            onFilterChange('maxBalanceDue', e.target.value)
                        }
                        margin="normal"
                        placeholder="0.00"
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start">
                                    <AttachMoney fontSize="small" />
                                </InputAdornment>
                            ),
                        }}
                    />
                </Box>

                {/* Action Buttons - Always visible */}
                <Box p={3} pt={2} borderTop={1} borderColor="divider">
                    <Box display="flex" justifyContent="space-between">
                        <Button
                            onClick={onReset}
                            color="secondary"
                            variant="outlined"
                        >
                            Reset
                        </Button>
                        <Button
                            onClick={onApply}
                            color="primary"
                            variant="contained"
                        >
                            Apply Filters
                        </Button>
                    </Box>
                </Box>
            </Box>
        </Drawer>
    )
}

export default TenantBillFilterDrawer
